/**
 * Die Wörter für die Zustände eines Geräts.
 *
 * **An einer Stelle, weil sie an zwei Orten stehen:** in der Zeile der
 * Geräteverwaltung und auf der Detailseite. Zwei Listen mit denselben Werten
 * laufen auseinander — und das war hier bereits geschehen: `modeLabel` in der
 * Geräteverwaltung prüfte auf `"local"`, ein Wert, den es nie gab
 * (`DEVICE_OPERATING_MODES` kennt `local_only`, `cloud`, `hybrid`). Ein Gerät im
 * reinen Lokalbetrieb stand dadurch als „Cloud" in der Liste — die genaue
 * Umkehrung dessen, was es tut.
 *
 * Deshalb sind es **vollständige Zuordnungen über den Vertragstyp** und keine
 * `if`-Ketten mit Rückfall: Kommt ein Wert hinzu, meldet der Typprüfer die
 * fehlende Zeile, statt ihn stillschweigend in den letzten Zweig laufen zu
 * lassen (Regel 9).
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { productLine } from "./device-type.js";

type Device = ApiComponents["schemas"]["Device"];
type OperatingMode = Device["operatingMode"];
type ClaimStatus = Device["claimStatus"];
type Reachability = Device["reachability"];
type DatapointAccess = ApiComponents["schemas"]["Datapoint"]["access"];

/**
 * Die Zugriffsrichtung eines Datenpunkts in Worten (ADR 0020).
 *
 * **„lesen" und „schalten", nicht „read" und „write".** Der Schlüssel steht in
 * Daten und Protokollen; auf der Seite steht, was jemand tun kann. `read_write`
 * heißt „beides" und nicht „lesen und schreiben" — ein Sollwert wird gelesen
 * *und* gesetzt, und das ist ein Zustand, keine Aufzählung zweier.
 */
export const DATAPOINT_ACCESS_LABELS: Record<DatapointAccess, string> = {
  read: "liest",
  write: "schaltet",
  read_write: "liest und schaltet",
};

/**
 * Die Erreichbarkeit in Worten — und in Farben.
 *
 * **Vier Zustände, weil „still" zwei Lagen verschwieg.** Ein Gerät, dessen
 * Verbindung nachweislich abgerissen ist, und eines, das am Broker als verbunden
 * gilt und trotzdem nichts meldet, standen beide grau daneben. Das erste ist
 * abgebaut oder stromlos, das zweite hängt, hat kein Netz mehr oder ist einem
 * Broker-Neustart zum Opfer gefallen — und nur beim zweiten lohnt es, gleich
 * noch einmal nachzusehen.
 *
 * **Grau ist kein blasses Rot.** `never_seen` ist der Normalfall unmittelbar
 * nach dem Koppeln: Es ist nichts kaputt, es hat nur noch niemand geredet. Eine
 * rote Marke wäre dort eine Falschmeldung, und wer sie einmal zu Unrecht gesehen
 * hat, glaubt der nächsten nicht mehr.
 */
export const REACHABILITY_LABELS: Record<Reachability, string> = {
  reachable: "erreichbar",
  overdue: "überfällig",
  unreachable: "nicht erreichbar",
  never_seen: "noch nie gemeldet",
};

/** Grün nur für das, was gerade wirklich sendet. */
export const REACHABILITY_TONES: Record<
  Reachability,
  "success" | "warning" | "danger" | "neutral"
> = {
  reachable: "success",
  overdue: "warning",
  unreachable: "danger",
  never_seen: "neutral",
};

/**
 * Die Betriebsart in Worten.
 *
 * **In der Web-App steht sie nirgends mehr** — sie ist eine Angabe für die
 * Mobile-App, die zwischen Cloud und Gerät im LAN wählt; hier ist die Antwort
 * immer „Cloud", und eine Marke, die an jedem Gerät dasselbe sagt, sagt nichts.
 * Geblieben ist der eine Fall, der hier etwas erklärt: `local_only` heißt, dass
 * dieses Gerät der Cloud nie etwas schickt — ohne diesen Satz stünde es
 * dauerhaft und unerklärt als still da (`deviceHints`).
 *
 * „Nur lokal" ist ausgeschrieben und nicht `local_only`: Es ist die Aussage, die
 * am meisten erklärt — dieses Gerät sendet nichts, und deshalb hängt es an
 * keiner Integration.
 */
export const OPERATING_MODE_LABELS: Record<OperatingMode, string> = {
  local_only: "nur lokal",
  cloud: "Cloud",
  hybrid: "Hybrid",
};

/**
 * Der Beanspruchungszustand in Worten.
 *
 * In der Liste steht er nicht — dort ist jedes Gerät beansprucht, sonst wäre es
 * nicht da. Auf der Detailseite gehört er trotzdem hin: `blocked` und
 * `released` sind die beiden Zustände, in denen jemand nach dem Grund sucht,
 * warum nichts mehr hereinkommt.
 */
export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  unclaimed: "nicht beansprucht",
  claimed: "beansprucht",
  released: "freigegeben",
  blocked: "gesperrt",
};

type ReportedSettings = NonNullable<Device["reportedSettings"]>;
type UnlinkReason = NonNullable<Device["unlinkReason"]>;

/**
 * Die Konfigurationsschlüssel des Geräts in Worten.
 *
 * **Vollständig über den Vertragstyp**, aus demselben Grund wie oben: Meldet
 * eine neue Firmware einen Schlüssel mehr und kommt er in den Vertrag, fehlt
 * hier eine Zeile — und der Typprüfer sagt es, statt dass die Oberfläche ihn
 * still verschweigt.
 *
 * Die Einheit steht im Wort, nicht im Wert: Das Gerät meldet `"60"` und
 * `"24"`, und eine nackte Zahl in einer Liste ist eine Einladung zum
 * Fehlschluss.
 */
export const SETTING_LABELS: Record<keyof ReportedSettings, string> = {
  deviceName: "Gerätename",
  meterNames: "Zählernamen",
  interval: "Sendetakt lokal (Sekunden)",
  mqttTopic: "Thema am lokalen Broker",
  haDiscovery: "Home-Assistant-Erkennung",
  extModule: "Zusatzschnittstelle",
  cloudEnabled: "Cloud-Anbindung",
  cloudInterval: "Sendetakt Cloud (Sekunden)",
  readInterval: "Auslesetakt Zähler (Sekunden)",
  cloudControl: "Fernsteuerung freigegeben",
  cloudSettings: "Einstellungen über die Cloud freigegeben",
  cloudDiag: "Diagnose freigegeben",
  cloudDiagDays: "Diagnosefrist (Tage, 0 = unbegrenzt)",
  cloudDiagUntil: "Diagnose freigegeben bis",
  cloudService: "Eingriffe freigegeben",
  otaChannel: "Update-Kanal",
  otaAuto: "Updates automatisch",
  otaCheckH: "Update-Prüfung (Stunden)",
};

/**
 * Warum ein Gerät sich abgemeldet hat.
 *
 * Beide Fälle sagen dasselbe über die Folge und Verschiedenes über den Anlass —
 * und der Anlass entscheidet, ob jemand etwas falsch verstanden hat: Ein
 * `remote` ohne dass jemand hier etwas ausgelöst hätte, wäre ein Vorfall.
 */
export const UNLINK_REASON_LABELS: Record<UnlinkReason, string> = {
  local: "am Gerät getrennt",
  remote: "aus der Cloud getrennt",
};

type DeviceCommand = ApiComponents["schemas"]["DeviceCommand"];
type CommandStatus = DeviceCommand["status"];
type CommandKind = DeviceCommand["kind"];
type DeviceAction = ApiComponents["schemas"]["SendDeviceCommandRequest"]["command"];

/**
 * Was aus einem Auftrag geworden ist, in Worten.
 *
 * **„Keine Antwort" und „nicht abgeschickt" sind zwei verschiedene Sätze**, und
 * der Unterschied ist der wichtigste dieser Liste: Im einen Fall hat das Gerät
 * nichts erfahren und die Handlung ist sicher nicht geschehen, im anderen kann
 * sie geschehen und nur die Quittung verloren gegangen sein. Bei einem Neustart
 * ist genau das der Regelfall.
 */
export const COMMAND_STATUS_LABELS: Record<CommandStatus, string> = {
  pending: "wird gesendet",
  sent: "abgeschickt, wartet auf Quittung",
  acknowledged: "ausgeführt",
  rejected: "vom Gerät abgelehnt",
  failed: "nicht abgeschickt",
  expired: "keine Antwort",
};

/** Die Färbung dazu — grün nur für das, was wirklich geschehen ist. */
export const COMMAND_STATUS_TONES: Record<
  CommandStatus,
  "success" | "warning" | "danger" | "neutral"
> = {
  pending: "neutral",
  sent: "neutral",
  acknowledged: "success",
  rejected: "danger",
  failed: "danger",
  expired: "warning",
};

/** Wovon ein Auftrag handelt. */
export const COMMAND_KIND_LABELS: Record<CommandKind, string> = {
  config: "Konfiguration",
  command: "Aktion",
  ota: "Update",
  /** Ein Wert an einen Datenpunkt (HM-110) — Lademodus, Stromgrenze, Relais. */
  dp: "Wert setzen",
};

/**
 * Die Aktionen in Worten — **benannt nach ihrer Wirkung, nicht nach dem
 * Protokollwort.** `publish` sagt einem Menschen nichts; „Werte jetzt senden"
 * sagt, was danach anders ist.
 */
export const DEVICE_ACTION_LABELS: Record<DeviceAction, string> = {
  diag: "Diagnose anfordern",
  publish: "Werte jetzt senden",
  hello: "Selbstauskunft anfordern",
  unlink: "Cloud-Verbindung am Gerät lösen",
  reboot: "Neu starten",
  "ap-on": "Hotspot einschalten",
  read: "Zähler jetzt auslesen",
};

/**
 * Was auf dem Knopf steht (UI-Durchsicht U7). Bis dahin hieß jeder „Auslösen" —
 * neun gleiche Knöpfe untereinander, und welcher was tut, stand nur in der Zeile
 * daneben. Das Verb allein reicht, weil die Zeile die Handlung schon nennt.
 */
export const DEVICE_ACTION_VERBS: Record<DeviceAction, string> = {
  diag: "Anfordern",
  publish: "Senden",
  hello: "Anfordern",
  unlink: "Lösen",
  reboot: "Neu starten",
  "ap-on": "Einschalten",
  read: "Auslesen",
};

/**
 * Warum das Gerät einen Auftrag abgelehnt hat.
 *
 * **Offene Liste mit bekannten Werten**, deshalb kein `Record` über einen Typ:
 * Eine neuere Firmware kann einen Grund hinzubekommen, und ihn dann als „–"
 * zu zeigen wäre schlechter, als den rohen Wert zu zeigen.
 */
const ACK_ERROR_LABELS: Record<string, string> = {
  unsupported: "Die Firmware kennt diesen Auftrag nicht.",
  denied: "Nicht erlaubt — der Schalter „Fernsteuerung“ ist am Gerät nicht gesetzt.",
  invalid: "Ein Wert war unplausibel. Das Gerät hat nichts geändert.",
  stale: "Am Gerät wurde inzwischen etwas umgestellt. Lade die Seite neu.",
  failed: "Die Ausführung ist fehlgeschlagen.",
};

export function ackErrorLabel(error: string): string {
  return ACK_ERROR_LABELS[error] ?? error;
}

/**
 * Was in der **Kopfleiste** steht, wenn ein Gerät geöffnet ist (HM-073).
 *
 * **Dieselbe Form wie `meterHeadline`** und aus demselben Grund (HM-072): Der
 * Entwurf legt Name und Kennzeichnung in die Kopfleiste des Rahmens, und die
 * Seite beginnt ohne eigene Kopfkarte. Der Rahmen ruft diese Funktion und weiß
 * nicht, wie ein Erzeugnis heißt.
 *
 * **Hersteller und Erzeugnis meldet das Gerät selbst** (`productLine`); der
 * Katalog bleibt der Rückfall für eines, das sich noch nicht gemeldet hat, und
 * die beiden Quellen werden nicht gemischt (HM-041).
 *
 * Die **Seriennummer** steht dazu, weil sie das ist, was man am Gehäuse abliest,
 * wenn man wissen will, ob man vor dem richtigen Gerät steht. Sie ist die
 * einzige Angabe der Unterzeile, die man abtippt — in der Kopfleiste steht sie
 * allerdings in der Fließschrift: Ein einzelnes Wort in Monoschrift mitten in
 * einer Zeile sieht aus wie ein Fehler, und zum Vergleichen steht sie unter
 * „Einstellungen" noch einmal.
 */
export function deviceHeadline(device: Device): { title: string; subtitle: string } {
  return { title: device.name, subtitle: `${productLine(device)} · ${device.serialNumber}` };
}

/**
 * Warum sich an dieser Anlage gerade **kein Gateway** verbinden lässt (HM-109).
 *
 * **Je Grund ein Weg hinaus.** Ein einzelnes „geht nicht" wäre die Auskunft,
 * die niemandem hilft: Der eine Fall verlangt ein gekauftes Gerät, der andere
 * das Freigeben des bestehenden Gateways. Deshalb antwortet der Server mit
 * einem **Grund** und nicht mit einem Boolean.
 *
 * Gelesen wird das im Kopplungsformular — dort, wo jemand einen Gateway-Code
 * eingegeben hat und die Frage sich wirklich stellt. Ein unbekannter Wert
 * bekommt keinen erfundenen Satz, sondern den allgemeinen: Ein neuer Grund
 * kommt aus dem Server, und diese Datei erfährt es erst beim nächsten
 * `pnpm gen:api`.
 */
export const GATEWAY_REFUSAL_LABELS: Record<string, string> = {
  site_has_gateway:
    "An dieser Anlage ist bereits ein Gateway verbunden. Gib es zuerst frei, " +
    "wenn du ein anderes verbinden möchtest.",
  no_manufactured_device:
    "Ein Gateway lässt sich mit dieser Anlage verbinden, sobald dort ein gekauftes " +
    "HausMeter-Gerät gekoppelt ist.",
};

export function gatewayRefusalLabel(refusal: string | null): string {
  if (refusal === null) return "Ein Gateway lässt sich mit dieser Anlage gerade nicht verbinden.";
  return (
    GATEWAY_REFUSAL_LABELS[refusal] ??
    "Ein Gateway lässt sich mit dieser Anlage gerade nicht verbinden."
  );
}
